/**
 * WCAG 2.2 AA Accessible Radio Button Group
 * Provides keyboard navigation and proper ARIA support for custom radio buttons
 */

// Add these methods to your Vue component or create a mixin
export const AccessibleRadioMixin = {
    data() {
        return {
            focusedItem: null,
            radioGroupItems: [], // Array of radio button IDs in this group
        };
    },

    mounted() {
        // Initialize radio group items from the DOM
        this.initializeRadioGroup();

        // Set initial tabindex values but don't set visual focus
        this.setInitialTabIndexes();
    },

    methods: {
        /**
         * Initialize radio group items for keyboard navigation
         */
        initializeRadioGroup() {
            // Find all radio inputs within this component
            const radioInputs = this.$el.querySelectorAll(
                'input[type="radio"]',
            );
            this.radioGroupItems = Array.from(radioInputs).map(
                (input) => input.id,
            );
        },

        /**
         * Set initial tabindex values without visual focus
         */
        setInitialTabIndexes() {
            // Use the component's method to check by ID if available, otherwise fallback to checkIsSelected
            const checkMethod =
                this.checkIsSelectedById || this.checkIsSelected;
            const checkedItem = this.radioGroupItems.find((id) =>
                checkMethod.call(this, id),
            );
            const focusableItem = checkedItem || this.radioGroupItems[0];

            // Set tabindex but don't set focusedItem to avoid visual focus
            this.updateTabIndexes(focusableItem);
        },

        /**
         * Set initial focus on the appropriate radio button (only when user enters group)
         */
        setInitialFocus() {
            // Use the component's method to check by ID if available, otherwise fallback to checkIsSelected
            const checkMethod =
                this.checkIsSelectedById || this.checkIsSelected;
            const checkedItem = this.radioGroupItems.find((id) =>
                checkMethod.call(this, id),
            );
            this.focusedItem = checkedItem || this.radioGroupItems[0];
        },

        /**
         * Handle keyboard navigation (WCAG 2.1.1)
         * @param {KeyboardEvent} event
         * @param {string} itemId
         */
        handleKeyDown(event, itemId) {
            const currentIndex = this.radioGroupItems.indexOf(itemId);
            let nextIndex;

            switch (event.key) {
                case 'ArrowDown':
                case 'ArrowRight':
                    event.preventDefault();
                    nextIndex =
                        (currentIndex + 1) % this.radioGroupItems.length;
                    this.changeSelected(this.radioGroupItems[nextIndex]);
                    this.focusAndSelectRadio(this.radioGroupItems[nextIndex]);
                    break;

                case 'ArrowUp':
                case 'ArrowLeft':
                    event.preventDefault();
                    nextIndex =
                        currentIndex === 0
                            ? this.radioGroupItems.length - 1
                            : currentIndex - 1;
                    this.changeSelected(this.radioGroupItems[nextIndex]);
                    this.focusAndSelectRadio(this.radioGroupItems[nextIndex]);
                    break;

                case 'Space':
                case 'Enter':
                    event.preventDefault();
                    this.changeSelected(itemId);
                    break;

                case 'Tab':
                    // Allow normal tab behavior but update focus tracking
                    if (event.shiftKey) {
                        this.moveFocusToPrevious();
                    } else {
                        this.moveFocusToNext();
                    }
                    break;

                case 'Home':
                    event.preventDefault();
                    this.focusAndSelectRadio(this.radioGroupItems[0]);
                    break;

                case 'End':
                    event.preventDefault();
                    this.focusAndSelectRadio(
                        this.radioGroupItems[this.radioGroupItems.length - 1],
                    );
                    break;
            }
        },

        /**
         * Focus and select a radio button
         * @param {string} itemId
         */
        focusAndSelectRadio(itemId) {
            // Find the radio input and focus it directly
            this.$nextTick(() => {
                const radioInput = this.$el.querySelector(
                    `input[id="${itemId}"]`,
                );
                if (radioInput) {
                    radioInput.focus();
                }
            });
        },

        /**
         * Handle focus events
         * @param {string} itemId
         */
        handleFocus(itemId) {
            // If no item was previously focused, this is the first entry into the group
            if (this.focusedItem === null) {
                this.setInitialFocus();
            } else {
                this.focusedItem = itemId;
            }

            // Update tabindex for proper focus management
            this.updateTabIndexes(itemId);
        },

        /**
         * Handle blur events
         * @param {string} itemId
         */
        handleBlur(itemId) {
            // Keep focus tracking but don't clear immediately
            // to support focus within the component
            setTimeout(() => {
                if (!this.$el.contains(document.activeElement)) {
                    this.focusedItem = null;
                }
            }, 100);
        },

        /**
         * Update tabindex values for proper focus management (WCAG 2.4.3)
         * @param {string} focusedItemId
         */
        updateTabIndexes(focusedItemId) {
            this.radioGroupItems.forEach((itemId) => {
                const radioInput = this.$el.querySelector(
                    `input[id="${itemId}"]`,
                );
                if (radioInput) {
                    if (itemId === focusedItemId) {
                        radioInput.setAttribute('tabindex', '0');
                    } else {
                        radioInput.removeAttribute('tabindex');
                    }
                }
            });
        },

        /**
         * Announce selection change to screen readers
         * @param {string} itemId
         */
        announceSelection(itemId) {
            const label = this.getLabelForId(itemId);
            const announcement = `${label} selected`;

            // Create or update live region for announcements
            let liveRegion = document.getElementById('radio-announcements');
            if (!liveRegion) {
                liveRegion = document.createElement('div');
                liveRegion.id = 'radio-announcements';
                liveRegion.setAttribute('aria-live', 'polite');
                liveRegion.setAttribute('aria-atomic', 'true');
                liveRegion.className = 'sr-only';
                document.body.appendChild(liveRegion);
            }

            liveRegion.textContent = announcement;
        },

        /**
         * Get label text for a radio button ID
         * @param {string} itemId
         * @returns {string}
         */
        getLabelForId(itemId) {
            const labelElement = this.$el.querySelector(
                `label[for="${itemId}"]`,
            );
            return labelElement ? labelElement.textContent.trim() : itemId;
        },

        /**
         * Move focus to next focusable element outside the radio group
         */
        moveFocusToNext() {
            // Implementation depends on your specific focus management needs
            const nextElement = this.getNextFocusableElement();
            if (nextElement) {
                nextElement.focus();
            }
        },

        /**
         * Move focus to previous focusable element outside the radio group
         */
        moveFocusToPrevious() {
            // Implementation depends on your specific focus management needs
            const prevElement = this.getPreviousFocusableElement();
            if (prevElement) {
                prevElement.focus();
            }
        },

        /**
         * Get next focusable element outside the radio group
         * @returns {Element|null}
         */
        getNextFocusableElement() {
            const focusableElements = this.getAllFocusableElements();
            const currentIndex = focusableElements.indexOf(this.$el);
            return currentIndex >= 0 &&
                currentIndex < focusableElements.length - 1
                ? focusableElements[currentIndex + 1]
                : null;
        },

        /**
         * Get previous focusable element outside the radio group
         * @returns {Element|null}
         */
        getPreviousFocusableElement() {
            const focusableElements = this.getAllFocusableElements();
            const currentIndex = focusableElements.indexOf(this.$el);
            return currentIndex > 0
                ? focusableElements[currentIndex - 1]
                : null;
        },

        /**
         * Get all focusable elements on the page
         * @returns {Element[]}
         */
        getAllFocusableElements() {
            const focusableElementsString =
                'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';
            const focusableElements = [
                ...document.querySelectorAll(focusableElementsString),
            ];

            return focusableElements.filter((element) => {
                return (
                    element.offsetWidth > 0 ||
                    element.offsetHeight > 0 ||
                    element === document.activeElement
                );
            });
        },

        /**
         * Validate radio group selection (for form validation)
         * @returns {boolean}
         */
        validateRadioGroup() {
            const hasSelection = this.radioGroupItems.some((id) =>
                this.checkIsSelected(id),
            );
            const isRequired = this.$el.querySelector('input[required]');

            if (isRequired && !hasSelection) {
                this.showValidationError();
                return false;
            }

            this.clearValidationError();
            return true;
        },

        /**
         * Show validation error with proper ARIA attributes
         */
        showValidationError() {
            // Add error styling and ARIA attributes
            const container = this.$el;
            container.setAttribute('aria-invalid', 'true');

            // Create or update error message
            let errorElement = container.querySelector('.radio-group-error');
            if (!errorElement) {
                errorElement = document.createElement('div');
                errorElement.className = 'radio-group-error';
                errorElement.setAttribute('role', 'alert');
                container.appendChild(errorElement);
            }

            errorElement.textContent = 'Please select an option';
        },

        /**
         * Clear validation error
         */
        clearValidationError() {
            const container = this.$el;
            container.removeAttribute('aria-invalid');

            const errorElement = container.querySelector('.radio-group-error');
            if (errorElement) {
                errorElement.remove();
            }
        },
    },
};
